import {
    Accordion,
    AccordionPanel,
    Box,
    CheckBox,
    FileInput,
    Form,
    FormField,
    Heading,
    MaskedInput,
    Paragraph,
    RadioButtonGroup,
    ResponsiveContext,
    Select,
    Text,
    TextArea,
    Tip,
} from "grommet";
import { useRouter } from "next/router";
import React, { useState, useContext, useEffect } from "react";
import CountryInput from "../../../components/CountryInput";
import Layout from "../../../components/layouts/EmptyLayout";
import MerchantHeader from "../../../components/MerchantHeader";
import StateInput from "../../../components/StateInput";
import {
    isOptionalPositiveNumber,
    isOptionalValidateUrl,
    validateEmail,
    validatePostalCode,
} from "../../../lib/validation";
import { handleErrors } from "../../../utils/errors";
import {
    getCountriesByPhoneCode,
    getCountryByPhoneCode,
    getPhoneMask,
    isValidPhoneNumberForCountries,
} from "../../../utils/phone";
import { getStateOptions } from "../../../utils/states";
import { DisplayInlineBlockBox, TextBasedIcon } from "../../../utils/styled";
import { flattenObject } from "../../../utils/object";
import StatusMessage from "../../../components/StatusMessage";
import StatusIcon from "../../../components/StatusIcon";
import { businessTypes } from "../../../utils/business";
import { MCC_CATEGORIES } from "../../../constants/BusinessType";
import { formatName } from "../../../utils/names";
import { getCompanyContact } from "../../../lib/company";
import PoweredByStreamlinedFooter from "../../../components/PoweredByStreamlinedFooter";
import { getSortFuncOnObjKey } from "../../../utils/sort";
import { faCircleCheck as IconCheck, faCircleExclamation as IconAlertCircle } from "@fortawesome/pro-light-svg-icons";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import AddPaymentMethod from "../../../components/pages/customers/AddPaymentMethod";
import Button from "../../../components/Button";
import { trackClick } from "../../../utils/segment";
import { QUICKPAY_SOURCE } from "../../../constants/QuickPayPaymentMethods";
import ConditionalWrapper from "../../../components/ConditionalWrapper";

const formSectionConfig = [
    {
        title: "Your information",
        name: "basic_information",
        formFields: [
            {
                label: "First name",
                name: "first_name",
                objName: "customer",
            },
            {
                label: "Last name",
                name: "last_name",
                objName: "customer",
            },
            {
                label: "Email",
                name: "email",
                validate: validateEmail,
                objName: "customer",
            },
            {
                label: "Phone",
                name: "code",
                component: MaskedInput,
                validate: (val) => {
                    if (!getCountryByPhoneCode(val)) return "invalid";
                },
                mask: [{ fixed: "+" }, { length: 3, regexp: /^[0-9]*$/, placeholder: "1" }],
                getAdditionalProps: (formValues, _, size) => {
                    return {
                        containerProps: { width: size === "small" ? "20%" : "14%" },
                    };
                },
            },
            {
                label: <Text>&nbsp;</Text>, // add an empty character so the form field doesn't get pushed down
                name: "primary_phone",
                component: MaskedInput,
                mask: getPhoneMask().formatted,
                objName: "customer",
                optional: true,
                getAdditionalProps: (formValues, _, size) => {
                    // The same area code can match multiple country
                    const matchedCountries = (getCountriesByPhoneCode(formValues?.code) || []).map(
                        (country) => country?.isoCode
                    );
                    const phoneCodeCountry = matchedCountries.length === 0 ? undefined : matchedCountries[0]; // will default to US mask
                    const countryToValidateAgainst = matchedCountries.length === 0 ? ["US"] : matchedCountries;

                    return {
                        mask: getPhoneMask(phoneCodeCountry, false).formatted,
                        containerProps: { width: size === "small" ? "80%" : "36%" },
                        validate: (val) => {
                            if (!val) return "required";
                            if (!isValidPhoneNumberForCountries(val, countryToValidateAgainst))
                                return "Invalid phone number";
                        },
                    };
                },
            },
            {
                label: "Business name",
                name: "company_name",
                objName: "customer",
            },
            {
                label: "Role at business",
                name: "role_at_business",
                objName: "customer",
            },
            {
                label: "I am authorized to make purchase decisions on behalf of the business stated above.",
                name: "authorized",
                component: CheckBox,
                fullWidth: true,
                defaultValue: false,
                contentProps: {
                    border: { color: "transparent" },
                    pad: "0",
                    margin: { top: "xsmall", horizontal: "0", bottom: "small" },
                },
            },
        ],
    },
    {
        title: "Business details",
        name: "business_details",
        formFields: [
            {
                label: "Country",
                name: "country",
                component: CountryInput,
                fullWidth: true,
                plain: true,
                objName: "billing_address",
            },
            {
                label: "Address line 1",
                name: "address1",
                objName: "billing_address",
            },
            {
                label: "Address line 2",
                name: "address2",
                optional: true,
                objName: "billing_address",
            },
            {
                label: "City",
                name: "city",
                fullWidth: true,
                objName: "billing_address",
            },
            {
                label: "State",
                name: "state",
                component: StateInput,
                plain: true,
                optional: true,
                getAdditionalProps: (formValues) => ({
                    // This is for props that hinge on the changing formValues
                    stateOptions: getStateOptions(formValues?.country || "US"),
                    label: formValues?.country === "US" ? "State" : "Region",
                    required: formValues?.country === "US",
                }),
                objName: "billing_address",
            },
            {
                label: "Postal code",
                name: "postal_code",
                optional: true,
                getAdditionalProps: (formValues) => {
                    return {
                        // This is for props that hinge on the changing formValues
                        validate: formValues?.country === "US" ? validatePostalCode : undefined, // validate when us
                        required: formValues?.country === "US",
                    };
                },
                objName: "billing_address",
                margin: { bottom: "large" },
            },
            {
                label: "Business category",
                name: "industry_category",
                component: Select,
                optional: true,
                options: MCC_CATEGORIES,
                labelKey: "label",
                valueKey: { key: "label", reduce: true },
                objName: "customer",
                onChange: (e, formValues, setFormValues) => {
                    // fetch industry category to display in the next field
                    const industry = e.target.value;
                    if (industry === "Other") {
                        setFormValues({ ...formValues, industry_category: industry, merchant_categories: [] });
                        return;
                    }
                    fetch(`/api/merchant-categories?mcc_range=${getMccCode(e.target.value)}`, {
                        method: "GET",
                    })
                        .then(handleErrors)
                        .then((categories) => {
                            const sortedCategories = categories.sort(getSortFuncOnObjKey("name"));
                            setFormValues({
                                ...formValues,
                                industry_category: industry,
                                merchant_categories: sortedCategories,
                            });
                        });
                },
                getAdditionalProps: (formValues, setFormValues, size) => ({
                    // This is for props that hinge on the changing formValues
                    fullWidth:
                        formValues.industry_category === "Other" ||
                        !(formValues?.merchant_categories && formValues?.merchant_categories.length > 0),
                    ...(size === "small" ? {} : { dropProps: { width: { max: "250px" } } }),
                }),
            },
            {
                label: "Sub category",
                name: "industry_sub_category",
                component: Select,
                optional: true,
                labelKey: "name",
                valueKey: { key: "name", reduce: true },
                objName: "customer",
                predicate: (formValues) =>
                    formValues?.merchant_categories &&
                    formValues?.merchant_categories.length > 0 &&
                    formValues.industry_category !== "Other",
                getAdditionalProps: (formValues, setFormValues, size) => ({
                    // This is for props that hinge on the changing formValues
                    options: formValues?.filtered_categories || formValues?.merchant_categories,
                    onSearch: (text) => {
                        if (!text) {
                            setFormValues({ ...formValues, filtered_categories: undefined });
                            return;
                        }
                        const escapedText = text.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
                        const exp = new RegExp(escapedText, "i");
                        const filtered_categories = formValues?.merchant_categories.filter((o) => exp.test(o.name));
                        setFormValues({ ...formValues, filtered_categories });
                    },
                    ...(size === "small" ? {} : { dropProps: { width: { max: "250px" } } }),
                }),
            },
            {
                label: "Type of business",
                name: "business_type",
                component: Select,
                optional: true,
                options: businessTypes,
                labelKey: "label",
                valueKey: { key: "value", reduce: true },
                objName: "customer",
                fullWidth: true,
            },
            {
                label: "Business website",
                name: "website",
                optional: true,
                component: MaskedInput,
                mask: [{ fixed: "https://" }, { regexp: /^.*$/ }],
                objName: "customer",
                validate: isOptionalValidateUrl,
            },
            {
                label: "Year in business",
                name: "years_in_business",
                optional: true,
                validate: isOptionalPositiveNumber,
                objName: "customer",
            },
            {
                label: "This business is tax exempt",
                name: "tax_exempt",
                optional: true,
                component: CheckBox,
                fullWidth: true,
                defaultValue: false,
                objName: "customer",
                contentProps: {
                    border: { color: "transparent" },
                    pad: "0"
                },
            },
            {
                label: "Upload reseller certificate",
                name: "reseller_certificate",
                optional: true,
                component: FileInput,
                fullWidth: true,
                maxSize: 10485760,
                objName: "customer",
                skipDefaultValue: true,
                predicate: (formValues) => !!formValues?.tax_exempt,
                getAdditionalProps: (formValues, setFormValues, size, handleFileSelect) => {
                    if (formValues['reseller_certificate']) {
                        handleFileSelect(formValues['reseller_certificate'])
                    }
                    //onChange: handleFileSelect
                },
            },
        ],
    },
    {
        title: "Billing details",
        name: "billing_details",
        isSubmit: true,
        formFields: [
            {
                label: "Who pays open invoices?",
                name: "has_ap_dept",
                component: RadioButtonGroup,
                fullWidth: true,
                options: [
                    {
                        label: "I pay invoices myself",
                        value: 0,
                    },
                    {
                        label: "Accounts payable",
                        value: 1,
                    },
                ],
                direction: "row",
                contentProps: {
                    border: { color: "transparent" },
                },
            },
            {
                label: "Contact first name",
                name: "billing_first_name",
                predicate: ({ has_ap_dept }) => !!has_ap_dept,
            },
            {
                label: "Last name",
                name: "billing_last_name",
                predicate: ({ has_ap_dept }) => !!has_ap_dept,
            },
            {
                label: "Phone",
                name: "billing_code",
                component: MaskedInput,
                validate: (val) => {
                    if (!getCountryByPhoneCode(val)) return "invalid";
                },
                mask: [{ fixed: "+" }, { length: 3, regexp: /^[0-9]*$/, placeholder: "1" }],
                predicate: ({ has_ap_dept }) => !!has_ap_dept,
                getAdditionalProps: (formValues, _, size) => {
                    return {
                        containerProps: { width: size === "small" ? "20%" : "14%" },
                    };
                },
            },
            {
                label: <Text>&nbsp;</Text>, // add an empty character so the form field doesn't get pushed down
                name: "billing_phone",
                component: MaskedInput,
                mask: getPhoneMask().formatted,
                optional: true,
                predicate: ({ has_ap_dept }) => !!has_ap_dept,
                getAdditionalProps: (formValues, _, size) => {
                    // The same area code can match multiple country
                    const matchedCountries = (getCountriesByPhoneCode(formValues?.billing_code) || []).map(
                        (country) => country?.isoCode
                    );
                    const phoneCodeCountry = matchedCountries.length === 0 ? undefined : matchedCountries[0]; // will default to US mask
                    const countryToValidateAgainst = matchedCountries.length === 0 ? ["US"] : matchedCountries;

                    return {
                        mask: getPhoneMask(phoneCodeCountry, false).formatted,
                        containerProps: { width: size === "small" ? "80%" : "36%" },
                        validate: (val) => {
                            if (!val) return "required";
                            if (!isValidPhoneNumberForCountries(val, countryToValidateAgainst))
                                return "Invalid phone number";
                        },
                    };
                },
            },
            {
                label: "Email",
                name: "billing_email",
                validate: validateEmail,
                predicate: ({ has_ap_dept }) => !!has_ap_dept,
            },
            {
                label: "Do you want to qualify for net terms?",
                name: "applied_for_net_terms",
                component: RadioButtonGroup, // missing options
                fullWidth: true,
                options: [
                    {
                        label: "Yes",
                        value: "Yes",
                    },
                    {
                        label: "No",
                        value: "No",
                    },
                ],
                direction: "row",
                contentProps: {
                    border: { color: "transparent" },
                },
                objName: "customer",
                predicate: (_, data) => !(data?.net_terms === 0) // hide if net terms is 0
            },
        ],
    },
    {
        title: "Set up QuickPay",
        name: "automatic_billing",
        asDiv: true,
        formFields: [
            {
                label: "",
                component: props => (
                    <AddPaymentMethod
                        prompt="Add a payment method for quick and easy billing. We’ll debit this account when your invoices are due, so you never have to worry about missed payments."
                        {...props}
                    />
                ),
                optional: true,
                customComponent: true, // render custom component instead of standard formfield
                fullWidth: true,
            },
        ],
        formSectionProps: {
            showCta: false
        },
    },
    {
        title: "Notes",
        name: "additional_information",
        isSubmit: true,
        isFinalSubmit: true, // additional check for submit on final step
        formSectionProps: {
            ctaProps: {
                label: "Submit application",
                fill: "horizontal",
            },
        },
        formFields: [
            {
                label: "Any additional notes?",
                name: "notes",
                component: TextArea,
                fullWidth: true,
                optional: true,
                objName: "customer",
            },
        ],
    },
];

const CustomerOnboarding = (props) => {
    const { companyDetails: data } = props;
    const router = useRouter();
    const { slug } = router.query;
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [mounted, setMounted] = useState(false);
    const size = useContext(ResponsiveContext);

    // Prevent hydration mismatch from ResponsiveContext (server doesn't know viewport size)
    useEffect(() => setMounted(true), []);

    // Show nothing during SSR/hydration to avoid ResponsiveContext mismatch
    if (!mounted) {
        return (
            <Layout hideFooter title="Loading">
                <Box align="center" pad="large" />
            </Layout>
        );
    }

    return data ? (
        <Layout hideFooter title={data?.business_name || formatName(
            { first_name: data?.contact_first_name, last_name: data?.contact_last_name },
            true
        )}>
            <Box align="center">
                <Box
                    pad={
                        size === "small"
                            ? { vertical: "small", horizontal: "small" }
                            : { top: "large", horizontal: "xlarge" }
                    }
                    width="large"
                    // Matches the deposit payer page (pages/d/[token].js). "large" put
                    // 48px between the merchant header and the card, which read as the
                    // header floating away from the content it introduces.
                    gap="medium"
                >
                    <MerchantHeader data={data} hideContact={size === "small"} centerAlignLogo={size === "small"} />
                    <Box border round="xsmall" elevation="medium">
                        <Box
                            border={{ side: "top", size: "large", color: data?.brand_color || "border" }}
                            round="xsmall"
                            pad="medium"
                        >
                            {isFormSubmitted ? (
                                <SubmissionSuccessScreen />
                            ) : (
                                <>
                                    <Heading level={1} size="medium">
                                        Application
                                    </Heading>
                                    <OnboardingForm data={data} onSuccess={() => setIsFormSubmitted(true)} slug={slug} />
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box margin={{ bottom: "large" }}>
                    {size === "small" && (
                        <Box fill="horizontal" pad={{ top: "small" }}>
                            <Paragraph textAlign="center" responsive={false} fill margin="none">
                                <Text textAlign="center" size="small">
                                    {formatName(
                                        { first_name: data?.contact_first_name, last_name: data?.contact_last_name },
                                        true
                                    )}{" "}
                                    ・ {data?.phone_number || ""}
                                    <br />
                                    {data?.email || ""}
                                </Text>
                            </Paragraph>
                        </Box>
                    )}
                    <PoweredByStreamlinedFooter showAllRightsReservedText />
                </Box>
            </Box>
        </Layout>
    ) : (
        <></>
    );
};

const SubmissionSuccessScreen = () => (
    <Box direction="column" margin={{ top: "large", bottom: "small" }} justify="center" align="center">
        <StatusIcon status="ok" size="xlarge" />
        <Heading level={1} size="medium">
            Thank you!
        </Heading>
        <Text margin={{ bottom: "medium" }} textAlign="center" size="medium">
            Your application has been received. <br />
            We will be in touch shortly.
        </Text>
    </Box>
);

const getMccCode = (categoryToLookup) => {
    const mccInd = MCC_CATEGORIES.findIndex((category) => category.label === categoryToLookup);
    return MCC_CATEGORIES[mccInd].mcc_code_range;
};

const getDefaultValue = (formFields) => {
    const defaultValues = {};
    formFields.forEach((formField) => {
        if (!formField.skipDefaultValue) defaultValues[formField.name] = formField.defaultValue || "";
    });
    return defaultValues;
};

const FormSection = ({
    option,
    index,
    initialFormValues,
    onSectionNext,
    ctaProps,
    onChange,
    onSkip,
    recipientId,
    showCta = true,
    data,
    handleFileSelect
}) => {
    const [formValues, setFormValues] = useState(initialFormValues || getDefaultValue(option?.formFields));
    const size = useContext(ResponsiveContext);
    return (
        <ConditionalWrapper
            condition={option?.asDiv}
            wrapperIfTrue={children => (<>{children}</>)}
            wrapperIfFalse={children => (
                <Form
                    onSubmit={onSectionNext}
                    onChange={(values) => {
                        setFormValues(values);
                        onChange && onChange(values);
                    }}
                    value={formValues}
                >
                    {children}
                </Form>
            )}
        >
            <DisplayInlineBlockBox
                fill="horizontal"
                pad={{ bottom: size === "small" ? "medium" : "small", horizontal: "xsmall" }}
                wrap
            >
                {option.formFields.map(
                    ({ fullWidth, optional, name, predicate, getAdditionalProps, onChange, ...item }) => {
                        const additionalProps =
                            getAdditionalProps && formValues ? getAdditionalProps(formValues, setFormValues, size, handleFileSelect) : {};
                        return (
                            (!predicate || predicate(formValues, data)) && (
                                <DisplayInlineBlockBox
                                    pad={{ right: "xsmall" }}
                                    width={
                                        fullWidth || additionalProps?.fullWidth || size === "small"
                                            ? { min: "100%", max: "100%" }
                                            : { min: "50%", max: "50%" }
                                    }
                                    key={`${name}-${index}`}
                                    {...(additionalProps?.containerProps || {})}
                                >
                                    {item.customComponent ? (
                                        <item.component
                                            recipientId={recipientId}
                                            successCallback={onSectionNext}
                                            data={data}
                                            onboardingFlag={true}
                                            onSkip={onSkip}/>
                                    ) : (
                                        <FormField
                                            name={name}
                                            required={!optional} // Required all field unless stated otherwise
                                            {...(name === 'reseller_certificate' ? {} : {
                                                onChange: (e) => onChange && onChange(e, formValues, setFormValues)
                                            })}
                                            {...item}
                                            {...additionalProps}
                                        />
                                    )}
                                </DisplayInlineBlockBox>
                            )
                        );
                    }
                )}
                {showCta && (
                    <Box
                        fill="horizontal"
                        direction="row"
                        align="end"
                        justify="end"
                        pad={{ horizontal: "xsmall" }}
                        margin={{ vertical: "small" }}
                    >
                        <Button
                            event={{section: 'onboarding_application', element: 'next', description: `step_${index+1}`}}
                            label={<Text margin={{ horizontal: "small" }}>Next</Text>}
                            type="submit"
                            fill={size === "small" ? "horizontal" : undefined}
                            primary // for the future if we need to set cta color, use `ctaColor` prop to set cta color and get rid of 'primary' prop
                            {...ctaProps}
                        />
                    </Box>
                )}
            </DisplayInlineBlockBox>
        </ConditionalWrapper>
    );
};

const OnboardingForm = ({ onSuccess, slug, data }) => {
    const [stepNum, setStepNum] = useState(0); // keeps track of which section we're on
    const [formValues, setFormValues] = useState({});
    const [formError, setFormError] = useState(); // set form error
    const [recipientId, setRecipientId] = useState(); // set form error
    const [formattedFormSectionConfig, setFormattedFormSectionConfig] = useState(formSectionConfig);
    const [completedSections, setCompletedSections] = useState(new Array(formattedFormSectionConfig.length).fill(false)); // keep track of which section is completed
    const [selectedFile, setSelectedFile] = useState(null); // track selected reseller certificate file

    useEffect(() => {
        let tempFormConfig = [];
        if (!data?.card_processing_enabled && !data?.bank_debit_processing_enabled) {
            tempFormConfig = formSectionConfig.filter((element) => element.title !== 'Set up QuickPay')
            setFormattedFormSectionConfig(tempFormConfig);
        }
    }, [data]);

    const handleFileSelect = (files) => {
        const file = files && files[0];
        if (file) { 
            setSelectedFile(file);
        } else {
        }
    };

    const renderPanelLabel = ({ title, ind, isCompleted, isDisabled, isError, isActive }) => (
        <ResponsiveContext.Consumer>
            {(size) => (
                <Box
                    align="center"
                    direction="row"
                    pad={{ vertical: size === "small" ? "medium" : "small", horizontal: "xsmall" }}
                    justify="between"
                    fill="horizontal"
                    gap="small"
                >
                    <Box align="center" direction="row" gap="small">
                        <TextBasedIcon
                            text={ind + 1}
                            highlight
                            border={{ color: isDisabled ? "text-weak" : "text" }}
                            size="medium"
                            width="32px"
                            height="32px"
                            color={isDisabled ? "text-weak" : "text"}
                            pad={{ top: "2px" }}
                        />
                        <Text size="large" color={isDisabled ? "text-weak" : "text"}>
                            {title}
                        </Text>
                    </Box>
                    {isError && (
                        <Tip content="Section contains error or incomplete information">
                            <Box>
                                <FontAwesomeIcon SelectedIcon={IconAlertCircle} color="status-critical" />
                            </Box>
                        </Tip>
                    )}
                    {isCompleted && !isError && !isActive && (
                        <FontAwesomeIcon SelectedIcon={IconCheck} color="status-ok" />
                    )}
                </Box>
            )}
        </ResponsiveContext.Consumer>
    );

    const onSubmit = async (value, isFinalSubmit=false) => {
        const submissionData = {
            customer: {},
            billing_address: {},
        };
        // copy over details if there's no ap department
        if (!value.has_ap_dept) {
            submissionData["billing_address"] = {
                first_name: value?.first_name,
                last_name: value?.last_name,
                email: value?.email,
                phone: `${value?.code} ${value?.primary_phone}`,
            };
        } else {
            submissionData["billing_address"] = {
                first_name: value?.billing_first_name,
                last_name: value?.billing_last_name,
                email: value?.billing_email,
                phone: `${value?.billing_code} ${value?.billing_phone}`,
            };
        }
        if (value?.industry_category === "Other") {
            value["industry_sub_category"] = undefined; // should not have industry sub category if category is Other
        }
        value["primary_phone"] = `${value?.code} ${value?.primary_phone}`;
        value["applied_for_net_terms"] = value?.applied_for_net_terms === "Yes"; // convert string into true and false. Grommet treats 'false' as a missing value and didn't pass required check.
        // construct submission data: need to separate formfields into two objects
        for (let i = 0; i < formSectionConfig.length; i++) {
            // loop through the sections
            const section = formSectionConfig[i];
            for (let j = 0; j < section.formFields.length; j++) {
                // loop through the form fields
                const formField = section.formFields[j];
                if (!("objName" in formField)) continue; // check which object to put the field in. If unspecified, ignore field
                // Skip reseller_certificate as it's handled separately via selectedFile
                if (formField.name === 'reseller_certificate') continue;
                submissionData[formField.objName][formField.name] = value[formField.name];
            }
        }
        if (isFinalSubmit || recipientId) {
            // Handle reseller certificate upload separately via files API using selectedFile
            if (recipientId && selectedFile) {
                // Upload reseller certificate via customer files API
                const fileFormData = new FormData();
                fileFormData.append('file', selectedFile);
                fileFormData.append('name', selectedFile.name);
                fileFormData.append('label', 'reseller_certificate');

                try {
                    await fetch(`/api/customers/${recipientId}/files`, {
                        method: "POST",
                        body: fileFormData,
                    });
                } catch (error) {
                    console.error('Failed to upload reseller certificate:', error);
                    // Continue with the main submission even if file upload fails
                }
            }

            await fetch(`/api/customers/${recipientId}/sessionless-update`, {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionData),
            })
                .then(handleErrors)
                .then((data) => {
                    if (isFinalSubmit) {
                        setFormError();
                        onSuccess && onSuccess(data)
                    } else {
                        setFormError();
                        completedSections[stepNum] = true;
                        setStepNum(stepNum + 1);
                    }
                })
                .catch((e) => setFormError(e));
        } else {
            await fetch(`/api/company/${slug}/submit-application`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionData),
            })
                .then(handleErrors)
                .then((data) => {
                    if (data && data.customer_uuid) {
                        setFormError();
                        setRecipientId(data.customer_uuid);
                        completedSections[stepNum] = true;
                        setStepNum(stepNum + 1);
                    } else {
                        setFormError(data);
                    }
                })
                .catch((e) => setFormError(e));
        }
    };

    return (
        <Box border={{ side: "top" }}>
            {formError && <StatusMessage apiError={formError} margin={{ top: "small" }} />}
            <Accordion
                activeIndex={stepNum}
                onActive={(inds) => {
                    // called when user toggle the accordion panels
                    if (!inds || inds.length === 0) return;
                    const newStep = inds[0];
                    // case 1: if user is going to previous section, allow it
                    if (newStep < stepNum) setStepNum(newStep);
                    // case 2: user want to go to sections after the next section, disallow it
                    if (newStep > stepNum + 1) return;
                    // case 3: user want to go to the next section, allow it only when current section is completed
                    completedSections[stepNum] && setStepNum(newStep);
                    trackClick({section: 'customer_onboarding', element: `step_${newStep+1}`});
                }}
            >
                {formattedFormSectionConfig.map((option, index) => (
                    <Box key={index} gap="xsmall">
                        <AccordionPanel
                            label={renderPanelLabel({
                                title: option?.title || "",
                                ind: index,
                                isCompleted: completedSections[index],
                                isDisabled: index > stepNum,
                                isActive: index == stepNum,
                            })}
                        >
                            <FormSection
                                option={option}
                                index={index}
                                initialFormValues={formValues[index]}
                                handleFileSelect={handleFileSelect}
                                onSectionNext={async (e) => {
                                    if (e && formValues) {
                                        const newFormValues = {
                                            ...formValues,
                                            [index]: e.value
                                        };
                                        setFormValues(newFormValues);
                                        if (option?.isSubmit) {
                                            if (option?.isFinalSubmit) {
                                                await onSubmit(flattenObject(newFormValues), true)
                                            } else {
                                                const success = await onSubmit(flattenObject(newFormValues));
                                                if (success) {
                                                    completedSections[index] = true; // if we're at this step, we know that validation passes
                                                    setStepNum(stepNum + 1);
                                                    return;
                                                } else {
                                                    return // something went wrong, don't move to next step
                                                }
                                            }
                                        }
                                    }
                                    completedSections[index] = true; // if we're at this step, we know that validation passes
                                    setStepNum(stepNum + 1);
                                }}
                                onSkip={() => {
                                    completedSections[index] = true; // if we're at this step, we know that validation passes
                                    setStepNum(stepNum + 1);
                                }}
                                onChange={(e) => {
                                    completedSections[index] = false;
                                }}
                                recipientId={recipientId}
                                data={{
                                    customer: {
                                        id: recipientId,
                                        company_name: formValues[0]?.company_name,
                                        card_processing_enabled: data?.card_processing_enabled,
                                        bank_debit_processing_enabled: data?.bank_debit_processing_enabled
                                    },
                                    ...data
                                }}
                                {...option.formSectionProps}
                            />
                        </AccordionPanel>
                    </Box>
                ))}
            </Accordion>
        </Box>
    );
};

export async function getServerSideProps({ query, res, req }) {
    const { slug } = query;
    const [getCompanyDetailsStatus, companyDetails] = await getCompanyContact(slug);

    // if customer details endpoint return 404, that means cusomter onboarding disabled
    if (getCompanyDetailsStatus === "404") {
        return {
            redirect: {
                permanent: false,
                destination: `/404`,
            },
        };
    }

    return {
        props: {
            companyDetails,
        },
    };
}
export default CustomerOnboarding;

CustomerOnboarding.auth = false;
